import React, { useEffect, useRef, useState } from "react";
import { Box, IconButton, Slider, Typography, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
// Iconsax — ClusterDen's standard icon library (see [[icon-library-iconsax]]); replaces the
// custom PlayButton/PauseButton SVG assets (Rahim, 2026-08-27, redesigning the voice-note
// player against a reference: better play icon + a volume glyph). Sized via the parent
// IconButton's own `"& svg"` sx rule below — a plain CSS descendant selector, so it applies to
// Iconsax's internal <svg> the same as it did the old asset.
import { Play, Pause, VolumeHigh } from "iconsax-react";
// Same "tap to load" download glyph ChatMediaCollapsedPreview.jsx uses for photo/video, reused
// here instead of a different Iconsax icon so all three media types match (Rahim: "use the
// everyday use one... so its symmetry").
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { useEchoMediaFetch } from "./useEchoMediaFetch";

function parseInitialDurationSeconds(initialDurationSeconds) {
  const n = Number(initialDurationSeconds);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.min(Math.round(n), 86400);
}

// "Loading forever" root cause (2026-08-27): this only checked "blob:" — a real embedded
// data: URI (used for the mock voice-note sample) is just as immediately available in memory
// as a blob, no network fetch needed, but fell through the same gap `isHttpUrl` also misses,
// so `mediaReady` never flipped true, the <audio> element's `src` never got set (see the
// effect below), and clicking play awaited a `loadedmetadata` event that could never fire.
function isBlobUrl(u) {
  const s = typeof u === "string" ? u.trim() : "";
  return s.startsWith("blob:") || s.startsWith("data:");
}

function isHttpUrl(u) {
  const s = typeof u === "string" ? u.trim() : "";
  return s.startsWith("http://") || s.startsWith("https://");
}

function ensureMetadata(audioEl) {
  return new Promise((resolve, reject) => {
    if (
      audioEl.readyState >= HTMLMediaElement.HAVE_METADATA &&
      Number.isFinite(audioEl.duration) &&
      audioEl.duration > 0
    ) {
      resolve(audioEl.duration);
      return;
    }
    const onMeta = () => {
      audioEl.removeEventListener("loadedmetadata", onMeta);
      audioEl.removeEventListener("error", onErr);
      resolve(Number.isFinite(audioEl.duration) && audioEl.duration > 0 ? audioEl.duration : 0);
    };
    const onErr = () => {
      audioEl.removeEventListener("loadedmetadata", onMeta);
      audioEl.removeEventListener("error", onErr);
      reject(new Error("audio metadata load failed"));
    };
    audioEl.addEventListener("loadedmetadata", onMeta);
    audioEl.addEventListener("error", onErr);
    audioEl.load();
  });
}

const AudioBox = ({
  audioUrl,
  initialDurationSeconds,
  mediaFileSizeBytes: _mediaFileSizeBytes,
  /** false = incoming / receiver bubble — use high-contrast controls in dark mode */
  fromMe = true,
  /** Task form preview on dark overlay — white controls; omit everywhere else */
  onDarkBackground = false,
  contactId,
  messageId,
  metaMediaId,
}) => {
  const theme = useTheme();

  const { resolvedUrl, isFetching, handleFetch } = useEchoMediaFetch(audioUrl, {
    contactId,
    messageId,
    tag: "AudioBox",
  });

  const audioRef = useRef(null);

  const [mediaReady, setMediaReady] = useState(() => isBlobUrl(audioUrl || ""));
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(() => parseInitialDurationSeconds(initialDurationSeconds) || 0);
  const [isLoading, setIsLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [hasError, setHasError] = useState(false);

  const parsedInitial = parseInitialDurationSeconds(initialDurationSeconds);
  const preloadMode = parsedInitial != null ? "none" : "metadata";

  useEffect(() => {
    const next = parseInitialDurationSeconds(initialDurationSeconds);
    if (next != null) {
      setDuration((d) => (d <= 0 || !Number.isFinite(d) ? next : d));
    }
  }, [initialDurationSeconds]);

  const audioUrl_ = resolvedUrl;

  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setHasError(false);
    setIsLoading(false);
    const blob = isBlobUrl(audioUrl_ || "");
    setMediaReady(blob);
    const el = audioRef.current;
    if (!el || !audioUrl_) return;
    if (blob) {
      el.src = audioUrl_;
      el.preload = preloadMode;
      try {
        el.load();
      } catch (_) {
        /* ignore */
      }
    } else {
      el.removeAttribute("src");
      try {
        el.load();
      } catch (_) {
        /* ignore */
      }
    }
  }, [audioUrl_, preloadMode]);

  if (!resolvedUrl && metaMediaId) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, padding: '5px', width: 250 }}>
        <IconButton onClick={handleFetch} disabled={isFetching} size="small" aria-label="Load audio">
          {isFetching ? <CircularProgress size={22} disableShrink /> : <FileDownloadOutlinedIcon />}
        </IconButton>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>Audio</Typography>
      </Box>
    );
  }

  const formatTime = (time) => {
    if (!Number.isFinite(time) || time <= 0) {
      return "0:00";
    }
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  const needsLoadGate = isHttpUrl(audioUrl_) && !mediaReady;

  const handleLoadFromMedia = async () => {
    const el = audioRef.current;
    if (!el || !audioUrl_ || mediaReady) return;
    setIsLoading(true);
    setHasError(false);
    try {
      el.src = audioUrl_;
      el.preload = parsedInitial != null ? "none" : "metadata";
      await ensureMetadata(el);
      if (Number.isFinite(el.duration) && el.duration > 0) {
        setDuration(el.duration);
      }
      setMediaReady(true);
    } catch (error) {
      console.error("Error loading audio:", error);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const togglePlayPause = async () => {
    if (needsLoadGate) return;
    const el = audioRef.current;
    if (!el) return;

    if (isPlaying) {
      el.pause();
      setIsPlaying(false);
      return;
    }

    setIsLoading(true);
    setHasError(false);
    try {
      await ensureMetadata(el);
      if (Number.isFinite(el.duration) && el.duration > 0) {
        setDuration(el.duration);
      }
      await el.play();
      setIsPlaying(true);
    } catch (error) {
      console.error("Error playing audio:", error);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) setCurrentTime(audioRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    const el = audioRef.current;
    if (!el) return;
    if (Number.isFinite(el.duration) && el.duration > 0) {
      setDuration(el.duration);
    }
    setIsLoading(false);
    setHasError(false);
  };

  const handleSeek = (event, newValue) => {
    if (!audioRef.current || needsLoadGate) return;
    audioRef.current.currentTime = newValue;
    setCurrentTime(newValue);
  };

  const sliderMax = duration > 0 ? duration : parsedInitial != null ? parsedInitial : 0;

  const s = typeof audioUrl_ === "string" ? audioUrl_.trim() : "";
  if (!s) {
    return (
      <Typography variant="caption" color="text.secondary">
        Audio unavailable
      </Typography>
    );
  }

  const receiverDarkContrast = fromMe === false && theme.palette.mode === "dark";
  const lightControls = onDarkBackground || receiverDarkContrast;

  const sliderSx = lightControls
    ? {
        flex: 1,
        minWidth: { xs: 70, sm: 72 },
        width: { xs: "auto", sm: "auto" },
        color: theme.palette.grey[200],
        height: { xs: "4px", sm: "4px" },
        padding: { xs: "6px 0", sm: "10px 0" },
        pointerEvents: needsLoadGate ? "none" : "auto",
        "& .MuiSlider-rail": {
          opacity: 1,
          bgcolor: "rgba(255,255,255,0.35)",
        },
        "& .MuiSlider-track": {
          bgcolor: theme.palette.common.white,
        },
        "& .MuiSlider-thumb": {
          width: { xs: "9px", sm: "10px" },
          height: { xs: "9px", sm: "10px" },
          backgroundColor: theme.palette.common.white,
          border: `1px solid ${theme.palette.grey[800]}`,
          boxShadow: "0 0 0 1px rgba(0,0,0,0.12)",
        },
      }
    : {
        // Brand teal accent (#1F7A5C) instead of flat black — matches the accent every other
        // redesigned bubble type uses (catalog/list/template), plus a real white scrubber
        // puck (was a solid black dot, easy to lose against the black track) per the
        // reference (Rahim, 2026-08-27).
        flex: 1,
        minWidth: { xs: 70, sm: 72 },
        width: { xs: "auto", sm: "auto" },
        color: "#1F7A5C",
        height: { xs: "4px", sm: "4px" },
        padding: { xs: "6px 0", sm: "10px 0" },
        pointerEvents: needsLoadGate ? "none" : "auto",
        "& .MuiSlider-rail": {
          opacity: 1,
          bgcolor: "rgba(0,0,0,0.15)",
        },
        "& .MuiSlider-track": {
          bgcolor: "#1F7A5C",
        },
        "& .MuiSlider-thumb": {
          width: { xs: "11px", sm: "12px" },
          height: { xs: "11px", sm: "12px" },
          backgroundColor: "#fff",
          border: "2px solid #1F7A5C",
          boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
        },
      };

  const controlIconSx = {
    color: onDarkBackground
      ? theme.palette.common.white
      : lightControls
        ? theme.palette.grey[100]
        : "#1F7A5C",
    // Button hit-box shrunk toward the icon's own size (was 48px around a 24px icon — ~12px
    // of dead space each side, Rahim 2026-08-27: "too much left and right spacing of the play
    // button reduce it").
    p: 0,
    minWidth: { xs: 28, sm: 34 },
    width: { xs: 28, sm: 34 },
    height: { xs: 28, sm: 34 },
    "& svg": {
      width: { xs: 18, sm: 24 },
      height: { xs: 18, sm: 24 },
      display: "block",
    },
    ...(lightControls
      ? {
          "& svg path": {
            fill: "currentColor",
          },
        }
      : {}),
  };

  const playPauseIconSx = {
    ...controlIconSx,
    "& svg": {
      width: { xs: 16, sm: 24 },
      height: { xs: 16, sm: 24 },
      maxWidth: { xs: 16, sm: "none" },
      maxHeight: { xs: 16, sm: "none" },
      display: "block",
    },
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: { xs: 0.375, sm: "8px" },
        backgroundColor: "transparent",
        padding: { xs: "2px 2px", sm: "5px" },
        width: { xs: "min(268px, 100%)", sm: "250px" },
        maxWidth: { xs: "100%", sm: "250px" },
        minWidth: 0,
        boxSizing: "border-box",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        {needsLoadGate ? (
          <IconButton
            onClick={handleLoadFromMedia}
            disabled={isLoading}
            aria-label="Load audio"
            size="small"
            sx={controlIconSx}
          >
            {isLoading ? (
              <CircularProgress
                disableShrink
                sx={{
                  color: onDarkBackground
                    ? theme.palette.common.white
                    : lightControls
                      ? theme.palette.grey[100]
                      : "#1F7A5C",
                  width: { xs: 18, sm: 24 },
                  height: { xs: 18, sm: 24 },
                }}
              />
            ) : (
              <FileDownloadOutlinedIcon />
            )}
          </IconButton>
        ) : (
          <IconButton
            onClick={togglePlayPause}
            disabled={isLoading}
            aria-label={isPlaying ? "Pause" : "Play"}
            size="small"
            sx={playPauseIconSx}
          >
            {isLoading ? (
              <CircularProgress
                disableShrink
                sx={{
                  color: onDarkBackground
                    ? theme.palette.common.white
                    : lightControls
                      ? theme.palette.grey[100]
                      : "#1F7A5C",
                  width: { xs: 18, sm: 24 },
                  height: { xs: 18, sm: 24 },
                }}
              />
            ) : isPlaying ? (
              <Pause size="100%" color="currentColor" variant="Bold" />
            ) : (
              <Play size="100%" color="currentColor" variant="Bold" />
            )}
          </IconButton>
        )}
      </Box>

      {/* Duration + volume glyph moved next to the play button, seek bar takes the rest of the
          row (Rahim, 2026-08-27, matching a reference: play → time → volume icon → progress
          bar, not time trailing at the far end). */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: needsLoadGate && parsedInitial == null ? "6px" : "5px",
          flexShrink: 0,
        }}
      >
        {needsLoadGate && parsedInitial == null ? (
          <Box
            component="span"
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              bgcolor: "#9e9e9e",
              flexShrink: 0,
            }}
          />
        ) : null}
        <Typography
          variant="caption"
          sx={{
            color: (t) => {
              if (needsLoadGate && parsedInitial == null) return "#E65100";
              if (onDarkBackground) return "#ffffff";
              if (lightControls) return t.palette.grey[300];
              return "#606060";
            },
            padding: { xs: "1px 2px", sm: "3px" },
            fontSize: { xs: "0.65rem", sm: "0.75rem" },
            lineHeight: { xs: 1.1, sm: 1.2 },
            whiteSpace: "nowrap",
          }}
        >
          {needsLoadGate
            ? parsedInitial != null
              ? formatTime(parsedInitial)
              : "Audio"
            : isPlaying
              ? formatTime(currentTime)
              : formatTime(duration > 0 ? duration : parsedInitial || 0)}
        </Typography>
        {!needsLoadGate && (
          <Box
            sx={{
              display: "flex",
              color: onDarkBackground
                ? theme.palette.common.white
                : lightControls
                  ? theme.palette.grey[100]
                  : "#1F7A5C",
              opacity: 0.85,
            }}
          >
            <VolumeHigh size={14} color="currentColor" variant="Bold" />
          </Box>
        )}
      </Box>

      <Slider
        value={needsLoadGate ? 0 : currentTime}
        max={sliderMax || 1}
        onChange={(e, v) => {
          if (!needsLoadGate) handleSeek(e, v);
        }}
        aria-label="Audio progress"
        sx={sliderSx}
      />

      <audio
        ref={audioRef}
        src={mediaReady && !needsLoadGate ? audioUrl_ : undefined}
        preload={needsLoadGate ? "none" : preloadMode}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onError={(e) => {
          console.error("Audio playback error:", e);
          setDuration(0);
          setIsLoading(false);
          setHasError(true);
        }}
      />
    </Box>
  );
};

export default AudioBox;
